<!--@format-->
<template>
    <div class="distributors-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Connect with your distributor</h3>
                <div class="svg-container widget-close-button" @click="formHasChanges">
                    <widget-close-svg />
                </div>
            </div>
            <div v-if="loading" class="loading-wrapper">
                <loading-glasshive />
                <p>Thanks! We're verifying your credentials. <br />This might take a sec.</p>
            </div>
            <div v-else class="content-wrapper">
                <div class="distributor-logo-container">
                    <img class="distributor-logo" :src="currentLogo" />
                    <p v-if="!hasError">
                        Enter your credentials to view quantity and availability for equipment sold
                        by this distributor.
                    </p>
                    <p v-else class="error-msg">
                        The credentials you provided didn't work. Please review your information and
                        try again!
                    </p>
                </div>
                <div class="form-wrapper">
                    <div
                        v-if="distributor === 'pax8' || distributor === 'ingramMicro'"
                        class="input-container"
                    >
                        <p class="custom-label">
                            Client ID
                            <span v-if="distributor === 'ingramMicro'">
                                (Located in your <em>Developer Portal</em>)
                            </span>
                        </p>
                        <app-input
                            v-model="$v.formFields.clientId.$model"
                            :is-valid="!submitted || $v.formFields.clientId.required"
                            placeholder="Client ID"
                            error="Client ID is required"
                        />
                    </div>
                    <div
                        v-if="distributor === 'pax8' || distributor === 'ingramMicro'"
                        class="input-container"
                    >
                        <p class="custom-label">
                            Client Secret
                            <span v-if="distributor === 'ingramMicro'">
                                (Located in your <em>Developer Portal</em>)
                            </span>
                        </p>
                        <app-input
                            v-model="$v.formFields.clientSecret.$model"
                            :type="showPassWord ? 'text' : 'password'"
                            :is-valid="!submitted || $v.formFields.clientSecret.required"
                            auto-complete="new-password"
                            placeholder="Client Secret"
                            error="Client Secret is required"
                        >
                            <div slot="inputIcon">
                                <component
                                    :is="showPassWord ? 'n16-hide-svg' : 'q5-email-open-svg'"
                                    @click.native="showHidePassword"
                                />
                            </div>
                        </app-input>
                    </div>
                    <div v-if="distributor === 'ingramMicro'" class="input-container">
                        <app-input
                            v-model="$v.formFields.customerNumber.$model"
                            placeholder="Customer Number"
                            label="Customer Number"
                            :is-valid="!submitted || $v.formFields.customerNumber.required"
                            error="Customer Number is required"
                        />
                    </div>
                    <div v-if="distributor === 'tdSynnex'" class="input-container">
                        <app-input
                            v-model="$v.formFields.accountNumber.$model"
                            placeholder="Account Number"
                            label="Account Number"
                            :is-valid="!submitted || $v.formFields.accountNumber.required"
                            error="Account Number is required"
                        />
                    </div>
                    <div
                        v-if="distributor === 'tdSynnex' || distributor === 'dandh'"
                        class="input-container"
                    >
                        <app-input
                            v-model="$v.formFields.username.$model"
                            placeholder="Username"
                            label="Username"
                            :is-valid="!submitted || $v.formFields.username.required"
                            error="Username is required"
                        />
                    </div>
                    <div
                        v-if="distributor === 'tdSynnex' || distributor === 'dandh'"
                        class="input-container"
                    >
                        <app-input
                            v-model="$v.formFields.password.$model"
                            placeholder="Password"
                            :type="showPassWord ? 'text' : 'password'"
                            auto-complete="new-password"
                            label="Password"
                            :is-valid="!submitted || $v.formFields.password.required"
                            error="Password is required"
                        >
                            <div slot="inputIcon">
                                <component
                                    :is="showPassWord ? 'n16-hide-svg' : 'q5-email-open-svg'"
                                    @click.native="showHidePassword"
                                />
                            </div>
                        </app-input>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="back-button" @click="formHasChanges">
                        {{ loading ? 'Cancel' : 'Go Back' }}
                    </app-button>
                </div>
                <div v-if="!loading" class="action-container">
                    <app-button class="save-button" @click="save()">
                        {{ isConnected ? 'Update' : 'Activate' }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppInput = () => import('@/components/common/app-input');
const LoadingGlasshive = () => import('@/assets/animations/loading-glasshive');
const N16HideSvg = () => import('@/components/svg/grid/n16-hide-svg');
const Q5EmailOpenSvg = () => import('@/components/svg/grid/q5-email-open-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');

import AppPopup from '@/components/popups/app-popup';
import EventBus from '@/scripts/event-bus.js';
import { required } from 'vuelidate/lib/validators';

export default {
    name: 'distributors-widget',

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'loading-glasshive': LoadingGlasshive,
        'n16-hide-svg': N16HideSvg,
        'q5-email-open-svg': Q5EmailOpenSvg,
        'widget-close-svg': WidgetCloseSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            loading: false,
            submitted: false,
            showPassWord: false,
            hasError: false,
            formFields: {
                username: null,
                password: null,
                accountNumber: null,
                clientId: null,
                clientSecret: null,
                customerNumber: null,
            },

            distributorLogos: {
                dandh: require('@/assets/images/d-and-h-logo.png'),
                ingramMicro: require('@/assets/images/ingram-micro-logo.png'),
                pax8: require('@/assets/images/pax-8-logo.png'),
                tdSynnex: require('@/assets/images/td-synnex-logo.png'),
            },

            distributorServices: {
                dandh: 'DandHIntegrations',
                ingramMicro: 'IngramMicroIntegrations',
                tdSynnex: 'TDSynnexIntegrations',
            },
        };
    },

    validations: {
        formFields: {
            username: {
                required,
            },

            password: {
                required,
            },

            accountNumber: {
                required,
            },

            clientId: {
                required,
            },

            clientSecret: {
                required,
            },

            customerNumber: {
                required,
            },
        },

        dandh: ['formFields.username', 'formFields.password'],
        tdSynnex: ['formFields.accountNumber', 'formFields.username', 'formFields.password'],
        pax8: ['formFields.clientId', 'formFields.clientSecret'],
        ingramMicro: [
            'formFields.clientId',
            'formFields.clientSecret',
            'formFields.customerNumber',
        ],
    },

    computed: {
        clientId() {
            return this.widget.clientId;
        },

        distributor() {
            return this.widget.distributor;
        },

        distributorData() {
            return this.widget.distributorData;
        },

        currentLogo() {
            return this.distributorLogos[this.distributor];
        },

        isConnected() {
            return this.distributorData.isConnected;
        },
    },

    created() {
        this.populateForm();
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        showHidePassword() {
            this.showPassWord = !this.showPassWord;
        },

        formHasChanges() {
            if (this.$v[this.distributor].$anyDirty) {
                this.showUnsavedChangesModal();
            } else {
                this.closeWidget();
            }
        },

        resetForm() {
            this.$v.$reset();
            this.closeWidget();
        },

        populateForm() {
            switch (this.distributor) {
                case 'dandh':
                    this.formFields.username = this.distributorData.username;
                    this.formFields.password = this.distributorData.password;
                    break;
                case 'ingramMicro':
                    this.formFields.clientId = this.distributorData.clientId;
                    this.formFields.clientSecret = this.distributorData.clientSecret;
                    this.formFields.customerNumber = this.distributorData.customerNumber;
                    break;
                case 'tdSynnex':
                    this.formFields.accountNumber = this.distributorData.accountNumber;
                    this.formFields.username = this.distributorData.username;
                    this.formFields.password = this.distributorData.password;
                    break;
                default:
                    break;
            }
        },

        save() {
            this.submitted = true;
            if (this.$v[this.distributor].$invalid) {
                return;
            } else {
                this.saveCredentials();
            }
        },

        generateDistributorPayload(distributor) {
            const payload = {
                ClientId: this.clientId,
            };

            switch (distributor) {
                case 'dandh':
                    payload.DandHUserName = this.formFields.username;
                    payload.DandHPassword = this.formFields.password;
                    break;
                case 'ingramMicro':
                    payload.IngramMicroClientId = this.formFields.clientId;
                    payload.IngramMicroClientSecret = this.formFields.clientSecret;
                    payload.IngramMicroCustomerNumber = this.formFields.customerNumber;
                    break;
                case 'tdSynnex':
                    payload.TDSynnexAccount = this.formFields.accountNumber;
                    payload.TDSynnexUserName = this.formFields.username;
                    payload.TDSynnexPassword = this.formFields.password;
                    break;
                default:
                    break;
            }

            return payload;
        },

        saveCredentials() {
            this.loading = true;
            const distributorPayload = this.generateDistributorPayload(this.distributor);
            const service = `/api/${this.distributorServices[this.distributor]}/Connect`;

            this.$api
                .post(service, distributorPayload)
                .then(() => {
                    this.showCredentialsCheckoutPopup();
                    EventBus.$emit('integrations:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    if (error.response.status === 400) {
                        this.hasError = true;
                    } else {
                        this.$helpers.showFailNotification(
                            'An error occurred while connecting with your distributor account.',
                            { error },
                        );
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        showCredentialsCheckoutPopup() {
            const content =
                '<p style="text-align:center">You can now view pricing and availability for this distributor. </br> </p>';

            this.$modal.show(
                AppPopup,
                {
                    type: 'success-popup success',
                    title: 'Your Credentials Check Out!',
                    content: content,
                    closeButton: true,
                },
                {
                    height: '212px',
                    textAlign: 'center',
                    width: this.$helpers.setPopupWidth(),
                    clickToClose: true,
                }
            );
        },

        showUnsavedChangesModal() {
            this.$modal.show(
                AppPopup,
                {
                    type: 'alert-popup',
                    title: 'Are you sure you want to leave?',
                    content:
                        '<p>There are unsaved changes. If you leave now, they will be lost. <br> Do you want to proceed with leaving?</p>',
                    actions: {
                        confirm: {
                            label: 'Discard Changes and Leave',
                            action: this.resetForm,
                        },
                        cancel: {
                            label: 'Stay',
                        },
                    },
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                }
            );
        },
    },
};
</script>

<style>
.distributors-widget .widget-contents-container .content-wrapper {
    padding: 24px;
}

.distributors-widget .widget-contents-container .loading-wrapper {
    display: flex;
    justify-content: center;
    overflow: auto;
    padding: 24px;
    position: relative;
    height: calc(100% - 152px);
    width: 100%;
}

.distributors-widget .widget-contents-container .loading-wrapper .loading-glasshive {
    height: 498px;
    width: 100%;
}

.distributors-widget .widget-contents-container .loading-wrapper > p {
    color: #382153;
    font: 700 22px/30px 'Nunito';
    text-align: center;
    position: absolute;
    top: 55%;
}

.distributors-widget .content-wrapper .distributor-logo-container {
    border-bottom: 1px solid #e9e9ef;
    color: #382153;
    font: normal normal normal 16px/24px 'Roboto';
    margin-bottom: 24px;
}

.distributors-widget .content-wrapper .distributor-logo-container .distributor-logo {
    margin-bottom: 16px;
    max-height: 45px;
    width: auto;
}

.distributors-widget .content-wrapper .distributor-logo-container .error-msg {
    color: #f30070;
}

.distributors-widget .content-wrapper .distributor-logo-container > p {
    margin-bottom: 24px;
}

.distributors-widget .form-wrapper {
    display: flex;
    flex-direction: column;
}

.distributors-widget .form-wrapper .input-container .custom-label,
.distributors-widget .form-wrapper .input-container .app-input label {
    color: #382153;
    font: 600 16px/24px 'Roboto';
    margin-bottom: 8px;
}

.distributors-widget .form-wrapper .input-container .custom-label span {
    font-weight: 400;
}

.distributors-widget .app-input {
    margin-bottom: 16px;
}

.distributors-widget .app-input.has-icon .input-container .icon-container {
    cursor: pointer;
    top: 8px;
    right: 16px;
}

.distributors-widget .app-input.has-icon .input-container .icon-container svg path,
.distributors-widget .app-input.has-icon .input-container .icon-container svg circle {
    fill: #cac6cb;
}

.distributors-widget .app-input.has-icon .input-container .icon-container svg line {
    stroke: #cac6cb;
}

.distributors-widget .widget-contents-container .back-button {
    border: 1px solid #cac6cb;
    font: 600 14px/25px 'Roboto';
    height: 40px;
    width: 127px;
}

.distributors-widget .widget-contents-container .save-button {
    height: 40px;
    width: 148px;
}
</style>
